import React, {
    useContext,
    useMemo,
    useState,
    createContext,
    PropsWithChildren,
} from 'react'
import { useApolloClient } from '@apollo/client'
import {
    AuthRoles,
    ClinicFieldsFragment,
    ClinicGroupFieldsFragment,
    EntityLinkFieldsFragment,
    EntityLinkType,
    MessageTemplateFieldsFragment,
    OrderByInput,
    QueryClinicGroupMessageTemplatesDocument,
    QueryClinicMessageTemplatesDocument,
    useCountClinicGroupMessageTemplatesQuery,
    useCountClinicMessageTemplatesQuery,
    useEntityLinksForClinicQuery,
    useQueryClinicGroupMessageTemplatesQuery,
    useQueryClinicMessageTemplatesQuery,
} from '~graphql/generated/graphql'
import { PageSizeType, SetStateFn } from '~config/constants'
import { useClinic } from '~contexts/clinic-context'
import { useUserContext } from '~config/user-context'
import { usePageControls } from '~utils/hooks/use-page-controls'
import { constructILike } from '~utils/querying'
import { useStateWithDebounce } from '~utils/hooks/use-state-with-debounce'
import { useBulkSelection } from '~utils/hooks/use-bulk-selection'

const DEFAULT_PAGE_SIZE: PageSizeType = 10

/**
 * The templates surface runs in one of two modes: a clinic's own templates,
 * or a clinic group's shared templates (which waterfall to linked clinic
 * copies — see entity links). Exactly one owner is set.
 */
interface Props {
    clinic?: ClinicFieldsFragment
    clinicGroup?: ClinicGroupFieldsFragment
    isOpen: boolean
    pageSize?: PageSizeType
}

export default function MessageTemplatesProvider({
    clinic,
    clinicGroup,
    children,
    isOpen,
    pageSize,
}: PropsWithChildren<Props>) {
    return (
        <MessageTemplatesContext.Provider
            value={{
                ...useMessageTemplateValues(
                    clinic,
                    clinicGroup,
                    isOpen,
                    pageSize ?? DEFAULT_PAGE_SIZE,
                ),
            }}
        >
            {children}
        </MessageTemplatesContext.Provider>
    )
}

export function useMessageTemplates() {
    return useContext(MessageTemplatesContext)
}

function useMessageTemplateValues(
    clinic: ClinicFieldsFragment | undefined,
    clinicGroup: ClinicGroupFieldsFragment | undefined,
    isOpen: boolean,
    initialPageSize: PageSizeType,
): MessageTemplatesContextType {
    const [query, setQuery, debouncedQuery] = useStateWithDebounce<string>('')
    const [orderBy, setOrderBy, debouncedOrderBy] =
        useStateWithDebounce<OrderByInput>(
            { column: 'created_at', isAscending: true },
            250,
        )
    const { page, setPage, pageSize } = usePageControls(
        initialPageSize,
        JSON.stringify([query, orderBy]),
    )

    const isGroupMode = !clinic && !!clinicGroup

    function handleSetQuery(query: string) {
        setQuery(query)
        setPage(0)
    }

    const where = debouncedQuery ? [constructILike('name', debouncedQuery)] : []

    const {
        data: currentData,
        previousData,
        refetch,
        loading,
    } = useQueryClinicMessageTemplatesQuery({
        variables: {
            clinicId: clinic?.id || '',
            where,
            page,
            pageSize,
            orderBy: debouncedOrderBy,
        },
        skip: !isOpen || !clinic,
    })
    const {
        data: currentCountData,
        previousData: previousCountData,
        loading: countLoading,
    } = useCountClinicMessageTemplatesQuery({
        variables: {
            clinicId: clinic?.id || '',
            where,
        },
        skip: !isOpen || !clinic,
    })

    const {
        data: currentGroupData,
        previousData: previousGroupData,
        refetch: refetchGroup,
        loading: groupLoading,
    } = useQueryClinicGroupMessageTemplatesQuery({
        variables: {
            clinicGroupId: clinicGroup?.id || '',
            where,
            page,
            pageSize,
            orderBy: debouncedOrderBy,
        },
        skip: !isOpen || !isGroupMode,
    })
    const {
        data: currentGroupCountData,
        previousData: previousGroupCountData,
        loading: groupCountLoading,
    } = useCountClinicGroupMessageTemplatesQuery({
        variables: {
            clinicGroupId: clinicGroup?.id || '',
            where,
        },
        skip: !isOpen || !isGroupMode,
    })

    // Keep the last rows on screen while a new sort / search / page loads.
    const data = currentData ?? previousData
    const countData = currentCountData ?? previousCountData
    const groupData = currentGroupData ?? previousGroupData
    const groupCountData = currentGroupCountData ?? previousGroupCountData
    const hasLoadedTemplates = isGroupMode
        ? !!groupData && !!groupCountData
        : !!data && !!countData

    // Clinic mode: resolve group-link status for every row in one query so
    // list rows can badge linked/unlinked templates without per-row lookups.
    const { data: linksData, refetch: refetchLinks } =
        useEntityLinksForClinicQuery({
            variables: {
                entityType: EntityLinkType.MessageTemplate,
                clinicId: clinic?.id || '',
            },
            skip: !isOpen || !clinic,
        })
    const linkByTemplateId = useMemo(
        () =>
            new Map(
                (linksData?.entityLinksForClinic ?? [])
                    .filter(link => link.child_id)
                    .map(link => [link.child_id as string, link]),
            ),
        [linksData],
    )

    const templates: MessageTemplateFieldsFragment[] = useMemo(
        () =>
            (isGroupMode
                ? groupData?.queryClinicGroupMessageTemplates
                : data?.queryClinicMessageTemplates) || [],
        [
            isGroupMode,
            data?.queryClinicMessageTemplates,
            groupData?.queryClinicGroupMessageTemplates,
        ],
    )

    const {
        selectedItems: selectedTemplates,
        toggleSelected,
        selectAllOnPage,
        selectExactItems,
        clearSelected,
        areAllOnPageSelected,
        isAllMatchingSelected,
    } = useBulkSelection(templates)

    const { isAdmin } = useUserContext()
    const { role, hasMultipleClinics } = useClinic()
    const apolloClient = useApolloClient()

    // Copy-across-clinics is a clinic-mode tool; group mode shares templates
    // through entity links (populate/waterfall) instead of ad-hoc copies.
    const canCopyAcrossClinics =
        !isGroupMode &&
        (isAdmin || (role === AuthRoles.ClinicManager && hasMultipleClinics))

    const [isBulkMode, setIsBulkMode] = useState(false)
    const [isSelectingAllMatching, setIsSelectingAllMatching] = useState(false)

    function enterBulkMode() {
        setIsBulkMode(true)
    }

    function exitBulkMode() {
        setIsBulkMode(false)
        clearSelected()
    }

    async function selectAllMatching() {
        const total =
            (isGroupMode
                ? groupCountData?.countClinicGroupMessageTemplates
                : countData?.countClinicMessageTemplates) || 0
        if (total === 0) return
        setIsSelectingAllMatching(true)
        try {
            if (isGroupMode && clinicGroup) {
                const result = await apolloClient.query({
                    query: QueryClinicGroupMessageTemplatesDocument,
                    variables: {
                        clinicGroupId: clinicGroup.id,
                        where,
                        page: 0,
                        pageSize: total,
                        orderBy: debouncedOrderBy,
                    },
                    fetchPolicy: 'network-only',
                })
                selectExactItems(
                    result.data?.queryClinicGroupMessageTemplates ?? [],
                )
            } else if (clinic) {
                const result = await apolloClient.query({
                    query: QueryClinicMessageTemplatesDocument,
                    variables: {
                        clinicId: clinic.id,
                        where,
                        page: 0,
                        pageSize: total,
                        orderBy: debouncedOrderBy,
                    },
                    fetchPolicy: 'network-only',
                })
                selectExactItems(
                    result.data?.queryClinicMessageTemplates ?? [],
                )
            }
        } finally {
            setIsSelectingAllMatching(false)
        }
    }

    function refetchTemplates() {
        if (isGroupMode) {
            refetchGroup()
        } else {
            refetch()
            refetchLinks()
        }
    }

    return {
        clinic,
        clinicGroup,
        isGroupMode,
        getLinkForTemplate: templateId => linkByTemplateId.get(templateId),

        templates,
        loading:
            !hasLoadedTemplates &&
            (isGroupMode
                ? groupLoading || groupCountLoading
                : loading || countLoading),
        totalTemplates:
            (isGroupMode
                ? groupCountData?.countClinicGroupMessageTemplates
                : countData?.countClinicMessageTemplates) || 0,
        refetchTemplates,

        canBulkSelect: canCopyAcrossClinics,
        canCopyAcrossClinics,

        isBulkMode,
        enterBulkMode,
        exitBulkMode,

        selectedTemplates,
        toggleSelected,
        selectAllOnPage,
        selectAllMatching,
        clearSelected,
        areAllOnPageSelected,
        isAllMatchingSelected,
        isSelectingAllMatching,

        query,
        setQuery: handleSetQuery,
        debouncedQuery,
        orderBy,
        setOrderBy,
        page,
        setPage,
        pageSize,
    }
}

type MessageTemplatesContextType = {
    /** Set in clinic mode; undefined when browsing a group's shared templates */
    clinic: ClinicFieldsFragment | undefined
    /** Set in group mode; the owner of shared (linkable) templates */
    clinicGroup: ClinicGroupFieldsFragment | undefined
    isGroupMode: boolean
    /**
     * Clinic mode: the group link (active or severed) for a template, if it
     * was ever linked. Always undefined in group mode.
     */
    getLinkForTemplate: (
        templateId: string,
    ) => EntityLinkFieldsFragment | undefined

    templates: MessageTemplateFieldsFragment[]
    /**
     * True only until the first result arrives. Later sort / search / page
     * changes keep the previous rows on screen while they load.
     */
    loading: boolean
    totalTemplates: number
    refetchTemplates: () => void

    /** Whether the current user can use bulk selection (i.e. has at least one bulk action available). */
    canBulkSelect: boolean
    /** Whether the current user has permission to copy templates across clinics. */
    canCopyAcrossClinics: boolean

    /** Whether the user has entered "bulk action" view (checkboxes visible, bulk action bar shown). */
    isBulkMode: boolean
    enterBulkMode: () => void
    /** Exits bulk mode and clears any current selection. */
    exitBulkMode: () => void

    selectedTemplates: MessageTemplateFieldsFragment[]
    toggleSelected: (template: MessageTemplateFieldsFragment) => void
    selectAllOnPage: (pageTemplates: MessageTemplateFieldsFragment[]) => void
    selectAllMatching: () => Promise<void>
    clearSelected: () => void
    areAllOnPageSelected: boolean
    isAllMatchingSelected: boolean
    isSelectingAllMatching: boolean

    query: string
    setQuery: (query: string) => void
    debouncedQuery: string
    orderBy: OrderByInput
    setOrderBy: SetStateFn<OrderByInput>
    page: number
    setPage: SetStateFn<number>
    pageSize: PageSizeType
}

const MessageTemplatesContext = createContext<MessageTemplatesContextType>(
    {} as MessageTemplatesContextType,
)
